/**
 * WorkersPanel — unified scraper-pool tracker
 * (SCRAPER-CONSOLIDATION-2026-05-12 + CONDENSE-2026-05-12).
 *
 * One left-rail card surfaces every tier in the HR scraping fleet so
 * the operator can glance at "what's firing right now?" Tiers ordered
 * by cadence (most frequent first):
 *
 *   1. vps-scraper   — kitchen-vps systemd timer, every 5 min
 *   2. mac-catalog   — Mac LaunchAgent, every 10 min (PRIMARY)
 *   3. parlay-scorer — Worker cron, every 15 min (STANDBY: crons=[])
 *   4. gh-actions    — GitHub Actions cron, every 30 min
 *
 * Each row: status dot · name · cadence pill · priority pill · age,
 * then compact hero stats (tracked / new this hour) and an inline
 * sparkline. STANDBY rows collapse to header-only.
 *
 * Data flow:
 *   /api/workers-status (console-worker)
 *     ← reads R2 hr_catalog/worker_stats/{,mac/,gh/}{latest,history}.json
 *   /api/vps-status (console-worker)
 *     ← reads R2 _console/vps-status.json (written by vps_reporter every 60s)
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type {
  WorkersStatus, WorkerSummary, VpsStatus, WorkerPass,
} from "../data/types";

export function renderWorkersPanel(): TemplateResult {
  const e   = store.get<WorkersStatus>("workersStatus");
  const vps = store.get<VpsStatus & { _stale?: boolean }>("vpsStatus");

  const rows: WorkerSummary[] = [];
  const vpsRow = buildVpsRow(vps);
  if (vpsRow) rows.push(vpsRow);
  for (const w of (e.data?.workers ?? [])) rows.push(w);
  rows.sort((a, b) => {
    const ca = a.cadence_seconds ?? Number.MAX_SAFE_INTEGER;
    const cb = b.cadence_seconds ?? Number.MAX_SAFE_INTEGER;
    if (ca !== cb) return ca - cb;
    return a.name.localeCompare(b.name);
  });

  if (rows.length === 0) {
    if (e.error && !vps.data) return panelError("Scrapers", e.error);
    if (e.loading || vps.loading) return panelSkeleton("Scrapers", 3);
    return panelStub("Scrapers", "no data — workers + vps endpoints unreachable");
  }

  const aliveCount  = rows.filter((r) => r.alive).length;
  const totalCount  = rows.length;
  const allAlive    = aliveCount === totalCount;
  const summaryColor = allAlive ? "var(--accent-green-light)"
                      : aliveCount > 0 ? "var(--accent-amber)"
                                        : "var(--accent-red-light)";

  return html`
    <section class="panel">
      <div class="panel__title">
        <span>Scrapers · ${totalCount} tiers</span>
        <span class="panel__title-actions" style="color:${summaryColor};">
          ${allAlive ? "all firing" : `${aliveCount}/${totalCount} firing`}
        </span>
      </div>
      ${rows.map(workerRow)}
    </section>
  `;
}

// Synthesize a WorkerSummary from vpsStatus. pulse_history (12 × 5-min
// buckets, newest-first) drives the sparkline; the rest of the catalog
// metrics stay at 0 since the VPS scrape doesn't track per-pass
// selection deltas — it's a CDP scraper, not a GraphQL catalog refresh.
function buildVpsRow(
  e: ReturnType<typeof store.get<VpsStatus & { _stale?: boolean }>>
): WorkerSummary | null {
  if (e.error || !e.data || e.data._stale) return null;
  const v = e.data;
  const lastScrapeMs = v.last_scrape_at ? Date.parse(v.last_scrape_at) : 0;
  const ageSec = lastScrapeMs ? Math.floor((Date.now() - lastScrapeMs) / 1000) : -1;
  const lastGateMs = v.last_gate_fire_at ? Date.parse(v.last_gate_fire_at) : 0;
  const gateAgeSec = lastGateMs ? Math.floor((Date.now() - lastGateMs) / 1000) : -1;
  const alive = gateAgeSec >= 0 && gateAgeSec <= 10 * 60;
  const rateLimited = alive && ageSec > 15 * 60;
  const role = rateLimited
    ? `${v.hostname} · HR rate-limit (data ${Math.floor(ageSec / 60)}m stale)`
    : `${v.hostname} · ${v.cadence === "prime" ? "PRIME cadence"
                                                : v.cadence === "off-peak" ? "OFF-PEAK cadence"
                                                : v.cadence}`;
  // Map pulse_history → WorkerPass[] so the existing sparkline renderer
  // can consume it. Each bucket spans 5 min; we encode the bucket count
  // as `new_selections` (the field the sparkline already reads).
  const pulse = v.pulse_history ?? [];
  const nowMs = Date.now();
  const bucketMs = 5 * 60 * 1000;
  const history: WorkerPass[] = pulse.map((count, i) => ({
    started_at:    new Date(nowMs - (i + 1) * bucketMs).toISOString(),
    finished_at:   new Date(nowMs - i * bucketMs).toISOString(),
    duration_ms:   0,
    events_scanned: count,
    events_failed:  0,
    new_selections: count,
    refreshed:      0,
    total_after:    0,
    aborted_early:  false,
  }));
  return {
    name:           "vps-scraper",
    role,
    alive,
    cadence_seconds: 300,
    priority:       "fallback",
    last_pass_at:   v.last_scrape_at,
    age_seconds:    ageSec >= 0 ? ageSec : (gateAgeSec >= 0 ? gateAgeSec : -1),
    duration_ms:    0,
    events_scanned: 0,
    events_failed:  0,
    new_selections: 0,
    refreshed:      0,
    total_after:    0,
    consecutive_429: 0,
    aborted_early:  false,
    abort_reason:   v.skip_reason ?? null,
    rolling: { passes: 0, total_new: 0, total_refreshed: 0, total_failed: 0 },
    history,
  };
}

function workerRow(w: WorkerSummary): TemplateResult {
  const ageSec = w.age_seconds;
  const cadence = w.cadence_seconds ?? 600;
  const dotCls = !w.alive ? "is-bad"
              : ageSec >= 0 && ageSec <= cadence ? "is-ok"
              : ageSec >= 0 && ageSec <= 2 * cadence ? "is-warn"
              : "is-bad";
  const ageLabel = ageSec < 0 ? "no passes"
                : ageSec < 60 ? `${ageSec}s ago`
                : ageSec < 3600 ? `${Math.floor(ageSec / 60)}m ago`
                : `${Math.floor(ageSec / 3600)}h ago`;
  const rolling = w.rolling ?? { passes: 0, total_new: 0, total_refreshed: 0, total_failed: 0 };
  const isStandby = w.priority === "standby";
  // VPS uses pulse-style history (per-bucket file-touch counts), so its
  // "new this hour" should be sum of buckets, not the dead 0 from the
  // synthesized rolling field. Detect VPS by name.
  const isVps = w.name === "vps-scraper";
  const vpsHourCount = isVps ? (w.history ?? []).reduce((s, p) => s + p.new_selections, 0) : 0;
  const sparkline = renderSparkline(w.history ?? []);

  return html`
    <div style="display:flex;flex-direction:column;gap:6px;
                padding:8px 0 4px 0;
                border-top:1px solid var(--border-1);">

      <!-- Header: identity + heartbeat -->
      <div style="display:flex;align-items:center;gap:8px;min-width:0;">
        <span class="status-dot ${dotCls} ${w.alive && ageSec >= 0 && ageSec <= cadence ? "is-pulse" : ""}"
              style="flex-shrink:0;"></span>
        <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:1px;">
          <div style="display:flex;align-items:center;gap:5px;
                       white-space:nowrap;overflow:hidden;">
            <span style="font-size:12.5px;color:var(--text-primary);font-weight:600;
                          overflow:hidden;text-overflow:ellipsis;min-width:0;
                          letter-spacing:-0.01em;">
              ${w.name}
            </span>
            ${cadencePill(w.cadence_seconds)}
            ${priorityPill(w.priority, w.alive)}
          </div>
          <div style="font-size:10px;color:var(--text-quaternary);
                       white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
                       line-height:1.3;">
            ${w.role}
          </div>
        </div>
        <span style="color:var(--text-tertiary);font-size:10.5px;
                      font-variant-numeric:tabular-nums;white-space:nowrap;
                      flex-shrink:0;">
          ${ageLabel}
        </span>
      </div>

      ${isStandby ? "" : html`
        <!-- Compact hero + sparkline strip — tighter than the original
              two-row layout so all 4 tiers fit without scrolling. -->
        <div style="display:grid;grid-template-columns:auto 1fr;gap:10px;
                     align-items:center;padding:2px 0 0;">
          <div style="display:flex;flex-direction:column;gap:1px;
                       font-variant-numeric:tabular-nums;line-height:1;">
            ${isVps ? html`
              <div style="display:flex;align-items:baseline;gap:6px;">
                <span style="font-size:17px;font-weight:700;
                              color:${vpsHourCount > 0 ? "var(--accent-green-light)" : "var(--text-tertiary)"};
                              letter-spacing:-0.02em;">
                  ${vpsHourCount}
                </span>
                <span style="font-size:9.5px;color:var(--text-quaternary);
                              font-weight:600;letter-spacing:0.06em;
                              text-transform:uppercase;">
                  files / hr
                </span>
              </div>
            ` : html`
              <div style="display:flex;align-items:baseline;gap:6px;">
                <span style="font-size:17px;font-weight:700;
                              color:var(--text-primary);
                              letter-spacing:-0.02em;">
                  ${formatNumber(w.total_after)}
                </span>
                <span style="font-size:9.5px;color:var(--text-quaternary);
                              font-weight:600;letter-spacing:0.06em;
                              text-transform:uppercase;">
                  tracked
                </span>
              </div>
              <div style="display:flex;align-items:baseline;gap:6px;">
                <span style="font-size:13px;font-weight:700;
                              color:${rolling.total_new > 0 ? "var(--accent-green-light)" : "var(--text-tertiary)"};
                              letter-spacing:-0.02em;">
                  ${rolling.total_new > 0 ? "+" : ""}${formatNumber(rolling.total_new)}
                </span>
                <span style="font-size:9.5px;color:var(--text-quaternary);
                              font-weight:600;letter-spacing:0.06em;
                              text-transform:uppercase;">
                  / hr
                </span>
              </div>
            `}
          </div>

          ${sparkline ? html`
            <div style="display:flex;flex-direction:column;gap:2px;
                         min-width:0;">
              <div style="flex:1;min-width:0;">${sparkline}</div>
              <div style="font-size:9px;color:var(--text-quaternary);
                           letter-spacing:0.05em;text-transform:uppercase;
                           font-weight:600;text-align:right;">
                ${isVps ? "60m" : `${rolling.passes}p · 60m`}
              </div>
            </div>
          ` : html`<div></div>`}
        </div>

        <!-- Compact last-pass detail strip (VPS hides — no per-pass deltas) -->
        ${isVps ? "" : html`
          <div style="display:flex;flex-wrap:wrap;gap:6px 10px;
                       padding:4px 0 0;
                       font-size:10px;color:var(--text-quaternary);
                       font-variant-numeric:tabular-nums;
                       letter-spacing:0.02em;">
            <span title="New selections in last pass">
              <span style="color:${w.new_selections > 0
                                      ? "var(--accent-green-light)"
                                      : "var(--text-tertiary)"};">
                ${w.new_selections > 0 ? "+" : ""}${w.new_selections}
              </span> new
            </span>
            <span title="Refreshed in last pass">
              ↻ <span style="color:var(--text-secondary);">${formatNumber(w.refreshed)}</span>
            </span>
            <span title="Events scanned in last pass">
              scanned <span style="color:var(--text-secondary);">${w.events_scanned}</span>
            </span>
            ${w.consecutive_429 > 0 ? html`
              <span style="color:var(--accent-amber);"
                    title="Consecutive HR 429 rate-limits">
                429 ×${w.consecutive_429}
              </span>
            ` : ""}
            ${w.aborted_early ? html`
              <span style="color:${_abortColor(w.abort_reason)};"
                    title="${_abortTooltip(w.abort_reason)}">
                ${_abortLabel(w.abort_reason)}
              </span>
            ` : ""}
            ${rolling.total_failed > 0 ? html`
              <span style="color:var(--accent-amber);">
                ${rolling.total_failed} fail/60m
              </span>
            ` : ""}
          </div>
        `}
      `}

      <!-- STANDBY tagline: one short line so the operator knows it's
            intentionally off, not failing. -->
      ${isStandby ? html`
        <div style="font-size:10.5px;color:var(--text-quaternary);
                     padding-left:18px;line-height:1.3;font-style:italic;">
          intentionally disabled — replaced by Mac + GH catalog pool
        </div>
      ` : ""}

      <!-- VPS skip-reason footer when present -->
      ${isVps && w.abort_reason ? html`
        <div style="font-size:10px;color:var(--accent-orange-light);
                     padding-left:18px;line-height:1.3;">
          ⚠ ${w.abort_reason}
        </div>
      ` : ""}
    </div>
  `;
}

// =============================================================
// Cadence + priority pills
// =============================================================
function cadencePill(seconds: number | undefined): TemplateResult {
  if (!seconds || seconds <= 0) return html``;
  const label = seconds < 60         ? `${seconds}s`
              : seconds < 3600       ? `${Math.round(seconds / 60)}m`
              :                        `${Math.round(seconds / 3600)}h`;
  return html`
    <span style="font-size:9px;font-weight:700;
                 letter-spacing:0.04em;text-transform:uppercase;
                 padding:1.5px 5px;border-radius:999px;
                 background:rgba(255,255,255,0.06);
                 color:var(--text-secondary);
                 border:1px solid var(--border-2);
                 flex-shrink:0;font-variant-numeric:tabular-nums;
                 line-height:1;">
      ${label}
    </span>
  `;
}

function priorityPill(p: WorkerSummary["priority"], alive: boolean): TemplateResult {
  if (!p) return html``;
  const palette: Record<NonNullable<WorkerSummary["priority"]>, [string, string, string]> = {
    primary:  ["rgba(48,209,88,0.10)",  "rgba(48,209,88,0.32)",  "var(--accent-green-light)"],
    fallback: ["rgba(10,132,255,0.10)", "rgba(10,132,255,0.32)", "var(--accent-blue-light)"],
    standby:  ["rgba(255,255,255,0.04)","var(--border-2)",        "var(--text-quaternary)"],
  };
  const [bg, br, fg] = palette[p];
  const label = p === "primary"  ? "P1"
              : p === "fallback" ? "P2"
              :                    "OFF";
  return html`
    <span style="font-size:9px;font-weight:700;
                 letter-spacing:0.04em;text-transform:uppercase;
                 padding:1.5px 5px;border-radius:999px;
                 background:${bg};color:${fg};
                 border:1px solid ${br};
                 flex-shrink:0;font-variant-numeric:tabular-nums;
                 line-height:1;
                 opacity:${alive || p === "standby" ? 1 : 0.65};">
      ${label}
    </span>
  `;
}

// Sparkline of NEW selections per pass — matches the "+N this hour"
// hero number above. Tighter height (18px vs old 22px) for the condensed
// layout. Single-point histories render as a flat baseline rather than
// an empty area so the visual rhythm stays consistent across rows.
function renderSparkline(history: WorkerSummary["history"]): TemplateResult {
  if (!history || history.length === 0) return html``;
  // newest-first → reverse for left-to-right time order
  const series = history.slice().reverse().map((p) => p.new_selections);
  if (series.length === 1) series.unshift(0);
  const maxV = Math.max(...series, 1);
  const w = 160;
  const h = 18;
  const stepX = series.length > 1 ? w / (series.length - 1) : 0;
  const pts = series.map((v, i) => {
    const x = i * stepX;
    const y = h - (v / maxV) * (h - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const areaPts = `0,${h} ${pts} ${w},${h}`;
  return html`
    <svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none"
         style="display:block;width:100%;height:${h}px;"
         title="New selections per pass (last 60m)">
      <polygon points="${areaPts}"
               fill="rgba(48,209,88,0.12)" stroke="none"/>
      <polyline points="${pts}" fill="none"
                stroke="var(--accent-green-light)" stroke-width="1.4"
                stroke-linejoin="round" stroke-linecap="round"/>
    </svg>
  `;
}

function _abortLabel(reason: string | null | undefined): string {
  if (!reason) return "aborted";
  if (reason === "discover_empty")  return "HR throttled discover";
  if (reason === "rate_limited")    return "HR rate-limit";
  if (reason === "batch_failed")    return "batch fail";
  if (reason === "budget_exceeded") return "time budget";
  if (reason === "all_filtered")    return "no scoped events";
  if (reason.startsWith("uncaught:")) return "worker error";
  return reason.length > 24 ? reason.slice(0, 24) + "…" : reason;
}
function _abortColor(reason: string | null | undefined): string {
  if (!reason) return "var(--accent-red-light)";
  if (reason === "discover_empty" || reason === "rate_limited" ||
      reason === "batch_failed"   || reason === "all_filtered") {
    return "var(--accent-amber)";
  }
  return "var(--accent-red-light)";
}
function _abortTooltip(reason: string | null | undefined): string {
  if (reason === "discover_empty")  return "HR rate-limited the discover query — worker is alive, will retry next minute";
  if (reason === "rate_limited")    return "HR rate-limited per-event fetches after 6 consecutive 429s";
  if (reason === "batch_failed")    return "Every event in a batch failed — likely HR rate-limit";
  if (reason === "budget_exceeded") return "Pass exceeded 30s wall-clock budget";
  if (reason === "all_filtered")    return "No events matched our allowed-comps scope";
  if (reason && reason.startsWith("uncaught:")) return `Worker threw: ${reason.slice(9)}`;
  return reason || "Pass aborted early";
}

function formatNumber(n: number): string {
  if (n == null || !Number.isFinite(n)) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000)    return `${(n / 1_000).toFixed(0)}k`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function panelSkeleton(title: string, rows: number): TemplateResult {
  return html`
    <section class="panel">
      <div class="panel__title"><span>${title}</span></div>
      ${Array.from({ length: rows }, () => html`
        <div class="skeleton" style="margin-bottom:6px;height:14px;"></div>
      `)}
    </section>
  `;
}

function panelStub(title: string, msg: string): TemplateResult {
  return html`
    <section class="panel">
      <div class="panel__title">
        <span>${title}</span>
        <span class="phase-stub-badge">offline</span>
      </div>
      <div style="font-size:12px;color:var(--text-quaternary);">${msg}</div>
    </section>
  `;
}

function panelError(title: string, err: Error): TemplateResult {
  return html`
    <section class="panel" style="border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>${title}</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="font-size:11px;color:var(--accent-red-light);
                  line-height:1.4;font-family:monospace;
                  word-break:break-all;white-space:pre-wrap;">
        ${err.message}
      </div>
    </section>
  `;
}
